// src/pages/Admins/components/AdminDetailModal.tsx
import React from "react";
import {Dialog, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {
  X,
  Shield,
  Mail,
  Phone,
  Calendar,
  ShieldCheck,
  AlertTriangle,
  Crown,
  Clock,
  Activity,
  Settings,
  Users,
  Home,
  MessageSquare,
} from "lucide-react";
import {formatDate} from "../../../utils/formatters";
import type {UserResponseDto} from "../../../types/users";

interface AdminDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  admin?: UserResponseDto | null;
}

// Badge Component
const Badge: React.FC<{
  children: React.ReactNode;
  variant: "success" | "warning" | "danger" | "info" | "purple";
}> = ({children, variant}) => {
  const variants = {
    success:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    warning:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
    danger: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    info: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    purple:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}
    >
      {children}
    </span>
  );
};

// Info Row Component
const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({icon, label, value}) => (
  <div className="flex items-start space-x-3 py-3">
    <div className="flex-shrink-0 text-gray-400">{icon}</div>
    <div className="flex-1">
      <div className="text-sm font-medium text-gray-900 dark:text-white">
        {label}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        {value}
      </div>
    </div>
  </div>
);

// Privilege Card Component
const PrivilegeCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  active: boolean;
}> = ({icon, title, description, active}) => (
  <div
    className={`rounded-lg p-4 border ${
      active
        ? "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800"
        : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
    }`}
  >
    <div className="flex items-start space-x-3">
      <div
        className={`p-2 rounded-lg ${
          active
            ? "bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300"
            : "bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
        }`}
      >
        {icon}
      </div>
      <div className="flex-1">
        <h4
          className={`text-sm font-medium ${
            active
              ? "text-purple-900 dark:text-purple-100"
              : "text-gray-900 dark:text-gray-100"
          }`}
        >
          {title}
        </h4>
        <p
          className={`text-xs mt-1 ${
            active
              ? "text-purple-700 dark:text-purple-300"
              : "text-gray-600 dark:text-gray-400"
          }`}
        >
          {description}
        </p>
      </div>
      {active && (
        <ShieldCheck className="w-4 h-4 text-purple-600 dark:text-purple-400" />
      )}
    </div>
  </div>
);

const AdminDetailModal: React.FC<AdminDetailModalProps> = ({
  isOpen,
  onClose,
  admin,
}) => {
  if (!admin) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="max-w-3xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Crown className="w-5 h-5 mr-2 text-yellow-500" />
                  Administrator Details
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Admin Profile */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {admin.avatar ? (
                      <img
                        className="h-16 w-16 rounded-full object-cover"
                        src={admin.avatar}
                        alt={admin.name}
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                        <Shield className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                      {admin.name}
                      <Crown className="w-4 h-4 ml-2 text-yellow-500" />
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                      Admin ID: {admin.id}
                    </p>
                    <div className="flex items-center space-x-2">
                      <Badge variant="purple">
                        <Shield className="w-3 h-3 mr-1" />
                        Administrator
                      </Badge>
                      <Badge variant={admin.isVerified ? "success" : "warning"}>
                        {admin.isVerified ? (
                          <ShieldCheck className="w-3 h-3 mr-1" />
                        ) : (
                          <AlertTriangle className="w-3 h-3 mr-1" />
                        )}
                        {admin.isVerified ? "Active" : "Pending Verification"}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                    Contact Information
                  </h4>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    <InfoRow
                      icon={<Mail className="w-4 h-4" />}
                      label="Email Address"
                      value={
                        <div className="flex items-center space-x-2">
                          <span>{admin.email}</span>
                          {admin.emailVerifiedAt && (
                            <span className="text-xs text-green-600 dark:text-green-400">
                              (Verified on {formatDate(admin.emailVerifiedAt)})
                            </span>
                          )}
                        </div>
                      }
                    />
                    <InfoRow
                      icon={<Phone className="w-4 h-4" />}
                      label="Phone Number"
                      value={admin.phone || "Not provided"}
                    />
                  </div>
                </div>

                {/* Admin Privileges */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                    Administrator Privileges
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PrivilegeCard
                      icon={<Users className="w-4 h-4" />}
                      title="User Management"
                      description="Create, edit, and manage user accounts"
                      active={admin.isVerified}
                    />
                    <PrivilegeCard
                      icon={<Home className="w-4 h-4" />}
                      title="Property Oversight"
                      description="Manage property listings and approvals"
                      active={admin.isVerified}
                    />
                    <PrivilegeCard
                      icon={<Settings className="w-4 h-4" />}
                      title="System Configuration"
                      description="Access to system settings and configuration"
                      active={admin.isVerified}
                    />
                    <PrivilegeCard
                      icon={<MessageSquare className="w-4 h-4" />}
                      title="Content Moderation"
                      description="Review and moderate user-generated content"
                      active={admin.isVerified}
                    />
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                    Account Information
                  </h4>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    <InfoRow
                      icon={<Calendar className="w-4 h-4" />}
                      label="Admin Since"
                      value={formatDate(admin.createdAt)}
                    />
                    <InfoRow
                      icon={<Clock className="w-4 h-4" />}
                      label="Last Login"
                      value={
                        admin.lastLogin
                          ? formatDate(admin.lastLogin)
                          : "Never logged in"
                      }
                    />
                    <InfoRow
                      icon={<Calendar className="w-4 h-4" />}
                      label="Last Updated"
                      value={formatDate(admin.updatedAt)}
                    />
                  </div>
                </div>

                {/* Security Notice */}
                {admin.isVerified ? (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h5 className="text-sm font-medium text-green-900 dark:text-green-300">
                          Active Administrator
                        </h5>
                        <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                          This administrator has full system access and verified
                          credentials. All admin actions are logged for security
                          purposes.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <h5 className="text-sm font-medium text-amber-900 dark:text-amber-300">
                          Pending Verification
                        </h5>
                        <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                          This administrator account is pending verification.
                          Some privileges may be restricted until verification
                          is complete.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AdminDetailModal;
